// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The canonical report view for a cycle. Renders every governed surface through the
// type-discipline primitives: numbers via CellValue (provenance on hover, not_found
// sentinels, quarantine hidden outside the forward-read panel), prose via ProseSlotBlock
// (figures interpolated from cell_refs, never parsed from text).
//
// The excess chart uses the ChartContainer TOKEN path — chartConfig colours are HSL over
// theme tokens (CHART_TOKENS), NOT hardcoded hex — so it themes correctly in dark mode.

import { useOutletContext } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine } from "recharts";
import { Card } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { HubSection, EmptyState } from "@/components/research-hub";
import {
  BenchmarkMatrix,
  AttributionTable,
  ReconciliationTable,
  CellValue,
  ProseSlotBlock,
  CHART_TOKENS,
  primaryPeriodKey,
  periodLabel,
} from "@/components/investment-reporting";
import type { Cell } from "@/types/ir";
import type { CycleContext } from "./ReportingCycleLayout";

export default function ReportTab() {
  const { cycle, ir } = useOutletContext<CycleContext>();
  if (!ir) return <EmptyState title="This cycle has no IR yet — nothing to report." />;

  const narrative = ir.narrative ?? {};
  const mc = ir.market_context;

  // The window THIS cycle actually computed. Derived from period_type, never hardcoded:
  // a monthly-interim cycle stamps its cells "m1" (app.py:710), so asking for "q2" here
  // rendered ~40 not_found sentinels per matrix over cells that were present and `ok`.
  const period = primaryPeriodKey(cycle?.period_type);

  // Excess vs the 3 benchmarks, P range — data resolved from governed Cells (value only).
  const excesses = ir.data?.excesses ?? {};
  const chartData = [1, 2, 3, 4, 5]
    .map((n) => `P${n}`)
    .filter((pid) => excesses[pid])
    .map((pid) => ({
      level: pid,
      cbam: (excesses[pid]?.vs_cbam?.[period] as Cell)?.value ?? null,
      afi: (excesses[pid]?.vs_afi?.[period] as Cell)?.value ?? null,
      arc: (excesses[pid]?.vs_arc?.[period] as Cell)?.value ?? null,
    }));

  // TOKEN path: config colours are theme tokens, not hex. ChartContainer injects each as
  // --color-<key>; the <Bar> marks reference var(--color-<key>).
  const chartConfig = {
    cbam: { label: "vs CBAM", color: CHART_TOKENS[0] },
    afi: { label: "vs AFI", color: CHART_TOKENS[1] },
    arc: { label: "vs ARC", color: CHART_TOKENS[2] },
  } as const;

  return (
    <div className="space-y-8">
      {/* Executive summary — prose, figures from cell_refs */}
      {narrative.exec_summary && (
        <HubSection title="Executive summary">
          <ProseSlotBlock slot={narrative.exec_summary} ir={ir} />
        </HubSection>
      )}

      {/* Performance vs the three benchmarks */}
      <HubSection title="Performance — P range (actively managed)">
        <Card className="p-4">
          <BenchmarkMatrix ir={ir} range="P" period={period} />
        </Card>
      </HubSection>
      <HubSection title="Performance — R range (index building blocks)">
        <Card className="p-4">
          <BenchmarkMatrix ir={ir} range="R" period={period} />
        </Card>
      </HubSection>

      {/* Excess chart — ChartContainer token path */}
      {chartData.length > 0 && (
        <HubSection title={`Excess vs benchmarks — P range, ${periodLabel(period)} (pp)`}>
          <Card className="p-4">
            <ChartContainer config={chartConfig} className="h-[320px] w-full">
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="level" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <ReferenceLine y={0} stroke="hsl(var(--foreground))" />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="cbam" fill="var(--color-cbam)" radius={2} />
                <Bar dataKey="afi" fill="var(--color-afi)" radius={2} />
                <Bar dataKey="arc" fill="var(--color-arc)" radius={2} />
              </BarChart>
            </ChartContainer>
          </Card>
        </HubSection>
      )}

      {/* Attribution (Brinson, R — time-weighted) */}
      <HubSection title="Attribution — why the R range lagged the composite">
        <Card className="p-4">
          <AttributionTable
            brinsonR={ir.attribution?.brinson?.R}
            caption="Time-weighted (snapshot-trap corrected). Allocation / selection decompose the gap vs the composite."
          />
        </Card>
      </HubSection>

      {/* Reconciliation — the primary gate input */}
      <HubSection title="Reconciliation — reconstruction vs FE actuals">
        <Card className="p-4">
          <ReconciliationTable reconciliation={ir.attribution?.reconciliation} />
        </Card>
      </HubSection>

      {/* Market scorecard */}
      {mc?.scorecard && (
        <HubSection title="Market read — independent scorecard">
          <Card className="p-4 space-y-2">
            <p className="font-body text-xs text-muted-foreground">
              {mc.scorecard.counts.confirm} confirm · {mc.scorecard.counts.nuance} nuance · {mc.scorecard.counts.overturn} overturned
            </p>
            <ul className="space-y-1.5">
              {mc.scorecard.rows.map((r, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-sm">
                  <span
                    className={
                      r.verdict === "confirm"
                        ? "text-primary"
                        : r.verdict === "nuance"
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-destructive"
                    }
                  >
                    [{r.verdict}]
                  </span>
                  <span className="min-w-0">
                    <span className="text-foreground">{r.cbam_claim}</span>{" "}
                    <span className="text-muted-foreground">— {r.evidence}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </HubSection>
      )}

      {/* Narrative sections */}
      {narrative.sections && (
        <HubSection title="What happened, and why">
          <div className="space-y-3">
            {Object.entries(narrative.sections as Record<string, any>).map(([key, slot]) => (
              <ProseSlotBlock key={key} slot={slot} ir={ir} />
            ))}
          </div>
        </HubSection>
      )}

      {/* Forward-read panel — the ONLY place quarantined cells may render */}
      {mc?.forward_read && (
        <HubSection title="Forward read (post-quarter — not Q2 attribution)">
          <Card className="p-4 border-amber-500/40 bg-amber-500/5 space-y-3">
            <p className="font-body text-[11px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
              {mc.forward_read.label}
            </p>
            <div className="flex flex-wrap gap-6">
              {mc.forward_read.stats.map((s, i) => (
                <div key={i} className="space-y-0.5">
                  {/* allowQuarantined — this labelled panel is the sanctioned context */}
                  <div className="font-heading text-lg">
                    <CellValue cell={s.value} allowQuarantined signed />
                  </div>
                  <div className="font-body text-xs text-foreground">{s.label}</div>
                  <div className="font-body text-[11px] text-muted-foreground">{s.sub}</div>
                </div>
              ))}
            </div>
          </Card>
        </HubSection>
      )}
    </div>
  );
}
